import type {
  MapState,
  MapStatusCode,
  MapStatusThemeItem,
  CameraTooltipThemeItem,
  CameraTooltipType,
  TooltipDebtState,
  TooltipStateThemeItem,
} from './types';

/**
 * Creates the initial mutable state container for the new map frontend runtime.
 *
 * @param params Capability flags resolved on page boot.
 * @returns Initial page state used by the modular frontend architecture.
 */
export function createMapState(params: {
  hasHoverCapability: boolean;
  hasFinePointer: boolean;
}): MapState {
  return {
    mapData: null,
    hoveredFieldId: null,
    hoveredCameraMapPositionId: null,
    hasHoverCapability: params.hasHoverCapability,
    hasFinePointer: params.hasFinePointer,
  };
}

/**
 * Resolves map status visual tokens from the validated map payload in runtime state.
 *
 * @param params Runtime state and API status code.
 * @returns Map status theme item.
 */
export function getMapStatusThemeFromState(params: {
  state: MapState;
  statusCode: MapStatusCode;
}): MapStatusThemeItem {
  if (params.state.mapData === null) {
    throw new Error('Тема карты недоступна: payload карты еще не загружен.');
  }

  return params.state.mapData.mapStatusTheme[params.statusCode];
}

/**
 * Resolves tooltip debt-state visual tokens from the validated map payload in runtime state.
 *
 * @param params Runtime state and API debt-state code.
 * @returns Tooltip state theme item.
 */
export function getTooltipStateThemeFromState(params: {
  state: MapState;
  debtState: TooltipDebtState;
}): TooltipStateThemeItem {
  if (params.state.mapData === null) {
    throw new Error('Тема тултипа недоступна: payload карты еще не загружен.');
  }

  return params.state.mapData.tooltipStateTheme[params.debtState];
}

/**
 * Resolves camera popup visual tokens from the validated map payload in runtime state.
 *
 * @param params Runtime state and API camera popup type.
 * @returns Camera popup theme item.
 */
export function getCameraTooltipThemeFromState(params: {
  state: MapState;
  tooltipType: CameraTooltipType;
}): CameraTooltipThemeItem {
  if (params.state.mapData === null) {
    throw new Error('Тема popup камеры недоступна: payload карты еще не загружен.');
  }

  return params.state.mapData.cameraTooltipTheme[params.tooltipType];
}
